import {
  tickValues,
  withBounds,
  withDerivedStep,
  withStep,
  type ScaleConfig,
} from '@anytracker/core'

import { Button } from '../../components/Button.js'
import { TextField } from '../../components/Field.js'
import styles from './TrackEditor.module.css'

interface Props {
  readonly scale: ScaleConfig
  readonly onChange: (scale: ScaleConfig) => void
}

/**
 * Min, max, and a step the app works out unless told otherwise (T-103).
 *
 * The derivation is the point: the user says "1 to 10" and gets a sensible
 * step without having to think about one, but the moment they type a step
 * themselves it stops moving under them. Both halves live in core —
 * `withBounds` re-derives only when the step was never explicit, `withStep`
 * marks it explicit — so this component chooses which to call and nothing more.
 */
export function NumericKindEditor({ scale, onChange }: Props) {
  const ticks = tickValues(scale)

  function numberFrom(raw: string, fallback: number): number {
    const parsed = Number(raw)
    return Number.isFinite(parsed) ? parsed : fallback
  }

  return (
    <>
      <div className={styles['pair']}>
        <TextField
          label="Minimum"
          type="number"
          inputMode="decimal"
          value={String(scale.min)}
          onChange={(event) =>
            onChange(
              withBounds(scale, numberFrom(event.target.value, scale.min), scale.max),
            )
          }
        />
        <TextField
          label="Maximum"
          type="number"
          inputMode="decimal"
          value={String(scale.max)}
          onChange={(event) =>
            onChange(
              withBounds(scale, scale.min, numberFrom(event.target.value, scale.max)),
            )
          }
        />
      </div>

      <div className={styles['pair']}>
        <TextField
          label="Step"
          type="number"
          inputMode="decimal"
          value={String(scale.step)}
          hint={scale.explicit.step ? 'Set by you' : 'Worked out from the range'}
          onChange={(event) =>
            onChange(withStep(scale, numberFrom(event.target.value, scale.step)))
          }
        />
        <TextField
          label="Decimal places"
          type="number"
          inputMode="numeric"
          min={0}
          max={6}
          value={String(scale.decimals)}
          onChange={(event) =>
            onChange({
              ...scale,
              decimals: Math.max(0, Math.round(numberFrom(event.target.value, 0))),
              explicit: { ...scale.explicit, decimals: true },
            })
          }
        />
      </div>

      {scale.explicit.step && (
        <div className={styles['derived']}>
          <span>The step no longer follows the range.</span>
          <Button variant="ghost" onClick={() => onChange(withDerivedStep(scale))}>
            Work it out again
          </Button>
        </div>
      )}

      {/*
        The actual values this scale produces, not a description of them.
        "Step 0.5" is abstract; seeing `0, 0.5, 1, 1.5 …` is how someone
        notices they have configured 200 positions on a slider.
      */}
      <div>
        <p className={styles['previewLabel']}>
          {ticks.length} possible {ticks.length === 1 ? 'value' : 'values'}
        </p>
        <p className={styles['ticks']}>
          {ticks.length <= 24
            ? ticks.map((tick) => tick.toFixed(scale.decimals)).join('  ')
            : `${ticks
                .slice(0, 8)
                .map((tick) => tick.toFixed(scale.decimals))
                .join('  ')}  …  ${ticks
                .slice(-3)
                .map((tick) => tick.toFixed(scale.decimals))
                .join('  ')}`}
        </p>
      </div>
    </>
  )
}
